/**
 * HEIC/HEIF を JPEG に変換し、それ以外はそのまま返す（クライアント側のみ）。
 * 極端に大きい原画のリサイズは別途検討（変換後も既存のサイズ上限で弾かれる）。
 */

export const JPEG_QUALITY = 0.92;

export const IMAGE_CONVERT_ERROR_MESSAGE =
    '変換に失敗しました。別形式でお試しください。';

/**
 * @param {File} file
 * @returns {boolean}
 */
export function isHeicLike(file) {
    const t = (file.type || '').toLowerCase();
    if (t === 'image/heic' || t === 'image/heif') {
        return true;
    }
    const n = file.name.toLowerCase();
    return n.endsWith('.heic') || n.endsWith('.heif');
}

/**
 * HEIC/HEIF のみ JPEG に変換。それ以外は同一参照で返す。
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function prepareImageFileForUpload(file) {
    if (!isHeicLike(file)) {
        return file;
    }

    try {
        const heic2any = (await import('heic2any')).default;
        const result = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: JPEG_QUALITY,
        });
        const blob = Array.isArray(result) ? result[0] : result;
        const baseName = file.name.replace(/\.(heic|heif)$/i, '');
        const jpegName = `${baseName || 'image'}.jpg`;
        return new File([blob], jpegName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
        });
    } catch (err) {
        console.error('HEIC conversion failed:', err);
        const e = new Error(IMAGE_CONVERT_ERROR_MESSAGE);
        e.cause = err;
        throw e;
    }
}

/**
 * メディアアップロード用: 動画・音声はそのまま。画像（および HEIC で MIME が空のもの）は HEIC のみ JPEG 化。
 * @param {FileList|File[]} fileList
 * @returns {Promise<File[]>}
 */
export async function prepareFileListForMedia(fileList) {
    const files = Array.from(fileList);
    const out = [];

    for (const file of files) {
        const t = (file.type || '').toLowerCase();
        if (t.startsWith('video/') || t.startsWith('audio/')) {
            out.push(file);
            continue;
        }
        out.push(await prepareImageFileForUpload(file));
    }

    return out;
}
